import React from 'react';
import { Box, Card, IconButton, Badge, Stack, Tooltip, Typography, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import type { FleetDevice } from '../../api/config';
import { HeartbeatSparkline } from './HeartbeatSparkline';
import { STATE_COLOR, STATE_TINT, pickDeviceState } from './LocationCard';

// Per-device card used in the Staging and Needs Attention sections at the top
// of the Fleet Dashboard. Same heartbeat sparkline as the operational card so
// admins can confirm the device is reporting; the event volume chart is
// intentionally omitted — staging devices do not produce inventory state
// events. Section identity comes from the surrounding header in
// FleetDashboardPage; the card itself is colored by data freshness so a
// silent staging gateway reads as a problem at a glance.

const HEARTBEAT_HEIGHT_PX = 26;

interface StagingDeviceCardProps {
  device: FleetDevice;
  nowMs: number;
  onOpenComments: (deviceId: number) => void;
}

export const StagingDeviceCard: React.FC<StagingDeviceCardProps> = ({ device, nowMs, onOpenComments }) => {
  // Solar low-vin is suppressed: these devices aren't in operational service,
  // so a flat battery isn't an alarm — pass false unconditionally.
  const state = pickDeviceState(device, nowMs, false);
  const label = device.label || device.coreid;
  const subline = device.location?.name ?? 'Unassigned';

  return (
    <Card
      variant="outlined"
      sx={{
        width: 272,
        p: 0.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        bgcolor: STATE_TINT[state],
        borderColor: STATE_COLOR[state],
        borderWidth: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, px: 0.25 }}>
        <Typography
          variant="subtitle2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={subline}
        >
          {subline}
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', px: 0.5, py: 0.25 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 18 }}>
          <MuiLink
            component={RouterLink}
            to={`/devices/${device.id}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: device.label ? undefined : 'monospace',
              fontSize: 11,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'text.primary',
            }}
            title={device.label ? `${device.label} (${device.coreid})` : device.coreid}
          >
            {label}
          </MuiLink>
          <Tooltip title={device.comments_count === 0 ? 'No comments — click to add' : `${device.comments_count} comment${device.comments_count === 1 ? '' : 's'}`}>
            <IconButton
              size="small"
              onClick={() => onOpenComments(device.id)}
              aria-label={`Comments for ${device.coreid}`}
              sx={{ p: 0.25 }}
            >
              <Badge
                badgeContent={device.comments_count}
                color="primary"
                overlap="circular"
                max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
              </Badge>
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing="4px" alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <HeartbeatSparkline
              publishes={device.publishes_24h}
              nowMs={nowMs}
              bucketSizeMinutes={60}
              bucketCount={24}
              bucketWidth={8}
              gap={1}
              height={HEARTBEAT_HEIGHT_PX}
            />
          </Box>
          <Stack
            direction="column"
            alignItems="flex-end"
            justifyContent="center"
            sx={{ width: 24, flexShrink: 0 }}
          >
            <Tooltip title="Total beacons heard in the most recent closed bucket">
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 10,
                  lineHeight: '12px',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {device.total_beacons}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};
